/**
 * AdminCharts Page
 * 
 * Chart management interface for administrators
 * 
 * Features:
 * - View all charts in the system
 * - Create new charts
 * - Edit existing charts
 * - Delete charts
 * - Search and filter
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ChartTable from '../components/ChartTable';
import ChartForm from '../components/ChartForm';
import '../assets/styles/AdminCharts.css';

// Use environment variable for API URL, fallback to localhost for local development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const AdminCharts = () => {
  const [charts, setCharts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingChart, setEditingChart] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCharts();
  }, []);

  /**
   * Fetch all charts from backend
   */
  const fetchCharts = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/getallCharts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCharts(data);
      } else {
        setError('Failed to load charts');
      }
    } catch (error) {
      console.error('Error fetching charts:', error);
      setError('Unable to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle chart creation
   */
  const handleCreateChart = async (formData) => {
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${API_URL}/admin/charts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        alert('Chart created successfully!');
        setShowForm(false);
        fetchCharts(); // Refresh the list
      } else {
        const data = await response.json();
        alert(`Error: ${data.error || 'Failed to create chart'}`);
      }
    } catch (error) {
      console.error('Error creating chart:', error);
      alert('Unable to create chart. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle chart update
   */
  const handleUpdateChart = async (formData) => {
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(
        `${API_URL}/admin/charts/${editingChart.id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      if (response.ok) {
        alert('Chart updated successfully!');
        setShowForm(false);
        setEditingChart(null);
        fetchCharts(); // Refresh the list
      } else {
        const data = await response.json();
        alert(`Error: ${data.error || 'Failed to update chart'}`);
      }
    } catch (error) {
      console.error('Error updating chart:', error);
      alert('Unable to update chart. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle chart deletion
   */
  const handleDeleteChart = async (chartId) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(
        `${API_URL}/admin/charts/${chartId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        alert('Chart deleted successfully!');
        fetchCharts(); // Refresh the list
      } else {
        const data = await response.json();
        alert(`Error: ${data.error || 'Failed to delete chart'}`);
      }
    } catch (error) {
      console.error('Error deleting chart:', error);
      alert('Unable to delete chart. Please try again.');
    }
  };

  /**
   * Handle edit button click
   */
  const handleEdit = (chart) => {
    setEditingChart(chart);
    setShowForm(true);
  };

  /**
   * Handle form cancel
   */
  const handleCancel = () => {
    setShowForm(false);
    setEditingChart(null);
  };

  /**
   * Handle new chart button
   */
  const handleNewChart = () => {
    setEditingChart(null);
    setShowForm(true);
  };

  /**
   * Filter charts by search term
   * Searches in title, tooltip, url, and tags
   */
  const filteredCharts = charts.filter(chart => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    
    // Check if search term matches title, tooltip, or url
    const matchesBasicFields = (
      chart.title.toLowerCase().includes(search) ||
      chart.tooltip.toLowerCase().includes(search) ||
      chart.url.toLowerCase().includes(search)
    );
    
    // Check if search term matches any tag
    const matchesTags = chart.tags && chart.tags.some(tag => 
      tag.toLowerCase().includes(search)
    );
    
    return matchesBasicFields || matchesTags;
  });

  return (
    <div className="admin-charts-container">
      {/* Header */}
      <header className="admin-charts-header">
        <div>
          <h1>Chart Management</h1>
          <p>Manage all charts available to users</p>
        </div>
        <button 
          className="back-btn" 
          onClick={() => navigate('/admin/dashboard')}
        >
          ← Back to Dashboard
        </button>
      </header>

      {/* Chart Form (Create/Edit) */}
      {showForm && (
        <div className="chart-form-section">
          <ChartForm
            chart={editingChart}
            onSubmit={editingChart ? handleUpdateChart : handleCreateChart}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </div>
      )}

      {/* Controls */}
      <div className="admin-charts-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search charts by title, tooltip, or URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <button 
          className="new-chart-btn"
          onClick={handleNewChart}
          disabled={showForm}
        >
          + Create New Chart
        </button>

        <div className="results-count">
          Showing {filteredCharts.length} of {charts.length} charts
        </div>
      </div>

      {/* Charts Table */}
      {isLoading ? (
        <div className="loading-state">
          <p>Loading charts...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchCharts}>Retry</button>
        </div>
      ) : (
        <ChartTable 
          charts={filteredCharts}
          onEdit={handleEdit}
          onDelete={handleDeleteChart}
          isUpdating={isSubmitting}
        />
      )}
    </div>
  );
};

export default AdminCharts;

